import React, { useState } from 'react';

const commentTypeBadge = (type) => {
  const styles = {
    revision_request: 'bg-orange-100 text-orange-700',
    note: 'bg-blue-100 text-blue-700',
    question: 'bg-primary-100 text-primary-700',
    manual_edit: 'bg-gray-100 text-gray-600',
  };
  return styles[type] || 'bg-gray-100 text-gray-600';
};

const statusBadge = (status) => {
  if (status === 'addressed') return 'bg-green-100 text-green-700';
  if (status === 'dismissed') return 'bg-gray-100 text-gray-500';
  return 'bg-orange-50 text-orange-600';
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const SectionCommentThread = ({ comments = [], sectionNumber, onAddComment, onDeleteComment, onRequestRevision, loading = false }) => {
  const [text, setText] = useState('');

  const sectionComments = comments.filter(c => c.section_number === sectionNumber);
  const openCount = sectionComments.filter(c => c.status === 'open').length;
  const openRevisionCount = sectionComments.filter(c => c.status === 'open' && c.comment_type === 'revision_request').length;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim() || loading) return;
    onAddComment({
      section_number: sectionNumber,
      comment: text.trim(),
      comment_type: 'revision_request',
    });
    setText('');
  };

  return (
    <div className="border-t border-gray-100 mt-2">
      {/* Comments list */}
      {sectionComments.length > 0 && (
        <div className="pt-2 space-y-2">
          {sectionComments.map((c) => (
            <div key={c.id} className="flex items-start gap-2 px-1 group">
              <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                {(c.user_name || 'U')[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-medium text-gray-800">{c.user_name || 'User'}</span>
                  {c.comment_type === 'manual_edit' && (
                    <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${commentTypeBadge(c.comment_type)}`}>
                      manual edit
                    </span>
                  )}
                  <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${statusBadge(c.status)}`}>
                    {c.status}
                  </span>
                  <span className="text-[10px] text-gray-400">{timeAgo(c.created_at)}</span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{c.comment}</p>
              </div>
              {c.status === 'open' && onDeleteComment && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDeleteComment(c.id); }}
                  className="shrink-0 mt-0.5 p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                  title="Remove comment"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New comment input */}
      {onAddComment && (
      <form onSubmit={handleSubmit} className="mt-2 pt-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary-400 focus:ring-0"
          />
          <button
            type="submit"
            disabled={!text.trim() || loading}
            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? '...' : 'Add'}
          </button>
        </div>
      </form>
      )}

      {openCount > 0 && (
        <div className="flex items-center justify-between mt-1">
          <p className="text-[10px] text-orange-500">{openCount} open comment{openCount !== 1 ? 's' : ''}</p>
          {openRevisionCount > 0 && onRequestRevision && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRequestRevision(sectionNumber); }}
              className="px-2.5 py-1 text-[11px] font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              Request Revision ({openRevisionCount})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionCommentThread;
